/* eslint-disable react-hooks/rules-of-hooks */
import Footer from '@/components/Footer';
import RightContent from '@/components/RightContent';
import { hasCodeInUrl, User } from '@/libs/oidc';
import { BookOutlined, LinkOutlined } from '@ant-design/icons';
import {
  MenuDataItem,
  SettingDrawer,
  Settings as LayoutSettings,
} from '@ant-design/pro-components';
import { Link, RunTimeLayoutConfig, useModel } from '@umijs/max';
import { PBAC } from 'pbac-validator';
import AccessibleRouteRedirect from './components/AccessibleRouteRedirect';
import { config as appConfig } from './config';
import { getAppContainer } from './libs/container';
import { SocketProvider } from './libs/context/SocketContext';
import { useUserManager } from './libs/hooks';
import { userCurrent } from './services/api-v3/account';
export * from '@/intercepts/request';

const isDev = process.env.NODE_ENV === 'development';

const publicPaths = ['/dynamic-report'];

const fetchUserInfo = async () => {
  try {
    const msg = await userCurrent();
    return msg;
  } catch (error) {
    return undefined;
  }
};

/**
 * Hàm xử lý lọc bỏ đi menu cha nếu tất cả các menu con không có permission
 *
 * @param menu
 * @param pbac
 * @returns
 */
const filterMenu = (menu: MenuDataItem[], pbac: PBAC): MenuDataItem[] => {
  return menu
    .map((item) => {
      // Xử lý menu con trước (nếu có)
      const children = item.routes as MenuDataItem[] | undefined;
      const filteredChildren =
        children && children.length > 0
          ? filterMenu(children, pbac)
          : undefined;

      // Kiểm tra permission của menu hiện tại
      const hasPermission =
        !item.access ||
        (() => {
          const [resource, action] = item.access.split('.');
          return pbac.evaluate({ resource, action });
        })();

      // Nếu menu có children đã được lọc
      if (filteredChildren !== undefined) {
        // Chỉ giữ menu cha nếu có ít nhất 1 menu con còn lại
        if (filteredChildren.length === 0) {
          return null;
        }
        return {
          ...item,
          routes: filteredChildren,
        };
      }

      // Menu không có children, chỉ kiểm tra permission
      return hasPermission ? item : null;
    })
    .filter((item): item is MenuDataItem => item !== null);
};

const isPublicPath = (path: string) => {
  return publicPaths.includes(path);
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  session?: User | null;
  user?: API.User | null;
}> {
  if (isPublicPath(window.location.pathname)) {
    // convert search query to object
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    const session = {
      access_token: token,
    } as any;
    if (token) {
      getAppContainer().session.set(session);
    }
    return {
      session,
      user: null,
      settings: {},
    };
  }
  const userManager = useUserManager();

  try {
    let session;

    if (hasCodeInUrl(window.location)) {
      session = await userManager.signinCallback();
      // remove code in url
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      session = await userManager.getUser();
    }

    if (!session || session.expired) {
      await userManager.signinRedirect();
      return {};
    }

    getAppContainer().session.set(session);

    // get user profile from API
    const user = await fetchUserInfo();

    if (!user) {
      await userManager.signoutRedirect();
      return {};
    }

    return {
      session,
      user,
      settings: {},
    };
  } catch (err) {
    await userManager.signoutRedirect();
    return {};
  }
}

// ProLayout supported api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    title: '',
    logo: '/logo.svg',
    rightContentRender: () => <RightContent />,
    disableContentMargin: false,
    menuDataRender: (menus: MenuDataItem[]): MenuDataItem[] => {
      const { user } = initialState ?? {};

      if (user?.role === 'super_admin') return menus;

      const pbac = new PBAC(user?.pbacStatements);
      return filterMenu(menus, pbac);
    },
    footerRender: () => <Footer />,
    childrenRender: (children, props) => {
      const { setInitialState } = useModel('@@initialState');

      return (
        <SocketProvider url={appConfig.socketHost} autoConnect={false}>
          <>
            {children}
            {!isPublicPath(props.location?.pathname) && (
              <SettingDrawer
                enableDarkTheme
                settings={initialState?.settings}
                onSettingChange={(settings) => {
                  setInitialState((preInitialState) => ({
                    ...preInitialState,
                    settings,
                  }));
                }}
              />
            )}
          </>
        </SocketProvider>
      );
    },
    links: isDev
      ? [
          <Link to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>Swagger</span>
          </Link>,
          <Link to="/~docs">
            <BookOutlined />
            <span>Docs</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    unAccessible: <AccessibleRouteRedirect />,
    ...initialState?.settings,
  };
};
